/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React from "react";

import { flattenDeep } from "lodash";

import { IListProps } from "@fluentui/react";
import { useConst } from "@fluentui/react-hooks";
import { DetailsRow, IGroup } from "@fluentui/react/lib/DetailsList";
import { GroupedList } from "@fluentui/react/lib/GroupedList";
import { Selection, SelectionMode, SelectionZone } from "@fluentui/react/lib/Selection";

import { IGroupItem } from "./IGroupItem";
import { IRegionalGroupedListProps } from "./RegionalGroupedListProps";

export const RegionalGroupedList: React.FunctionComponent<IRegionalGroupedListProps> = (
  props: IRegionalGroupedListProps
) => {
  const { geoData, selectedData, onSelectionChanged } = props;

  const buildGroups = (): IGroup[] => {
    let startIndex = 0;
    const groups = geoData.map((clRegion, _) => {
      const hasStates = clRegion.countryRegions?.some((cr) => cr.stateProvinces.length > 0);
      const group = {
        count: clRegion.countryRegions?.length,
        key: `group-${clRegion.clRegionId}`,
        name: clRegion.clRegionName,
        startIndex: startIndex,
        level: 1,
        children: hasStates
          ? clRegion.countryRegions
              ?.map((cr) => {
                if (cr.stateProvinces.length === 0) {
                  const groupLevel2WithoutStates = {
                    count: 1,
                    key: `group-${clRegion.clRegionId}-${cr.isoShort}`,
                    name: cr.countryRegionName,
                    startIndex: startIndex,
                    level: 2,
                  } as IGroup;
                  startIndex = startIndex + 1;
                  return groupLevel2WithoutStates;
                }
                const groupLevel2 = {
                  count: cr.stateProvinces.length,
                  key: `group-${clRegion.clRegionId}-${cr.isoShort}`,
                  name: cr.countryRegionName,
                  startIndex: startIndex,
                  level: 2,
                } as IGroup;

                startIndex = startIndex + cr.stateProvinces.length;

                return groupLevel2;
              })
              .filter((cr) => cr !== null)
          : [],
      } as IGroup;

      if (!hasStates) {
        startIndex = startIndex + (clRegion.countryRegions?.length ?? 0);
      }

      return group;
    });

    const global = {
      count: startIndex,
      key: "group-global",
      name: "Global",
      startIndex: 0,
      level: 0,
      children: groups,
    };

    return [global];
  };

  const buildItems = (): IGroupItem[] => {
    const itemGroups: any = geoData.map((clr) => {
      return clr.countryRegions?.map((cr) => {
        if (cr.stateProvinces !== undefined && cr.stateProvinces?.length > 0) {
          return cr.stateProvinces?.map((sp) => {
            return {
              key: `clRegion-${clr.clRegionName}-countryRegionName-${cr.countryRegionName}-stateProvinceName-${sp.stateProvinceName}`,
              clRegionName: clr.clRegionName,
              clRegionId: clr.clRegionId,
              regionName: cr.countryRegionName,
              regionIsoCode: cr.isoShort,
              stateProvinceName: sp.stateProvinceName,
              stateProvinceId: sp.stateProvinceId,
            };
          });
        }

        return [
          {
            key: `clRegion-${clr.clRegionName}-countryRegionName-${cr.countryRegionName}-stateProvinceName-null`,
            clRegionName: clr.clRegionName,
            clRegionId: clr.clRegionId,
            regionName: cr.countryRegionName,
            regionIsoCode: cr.isoShort,
            stateProvinceName: cr.countryRegionName,
            stateProvinceId: cr.isoShort,
          },
        ];
      });
    });

    return flattenDeep<IGroupItem[]>(itemGroups);
  };

  const groups = buildGroups();
  const items = buildItems();

  const selection = useConst(() => {
    const s = new Selection({
      onSelectionChanged: (): void => {
        if (onSelectionChanged) {
          onSelectionChanged(s.getSelection() as IGroupItem[]);
        }
      },
    });
    s.setItems(items, true);

    if (!!selectedData) {
      selectedData.forEach((element) => {
        s.setKeySelected(
          `clRegion-${element.clRegionName}-countryRegionName-${element.countryOrRegionName}-stateProvinceName-${element.stateOrProvinceName}`,
          true,
          true
        );
      });
    }

    return s;
  });

  const onRenderCell = (
    nestingDepth?: number,
    item?: IGroupItem,
    itemIndex?: number
  ): React.ReactNode => {
    return item && typeof itemIndex === "number" && itemIndex > -1 ? (
      <DetailsRow
        columns={[
          {
            key: "stateProvinceName",
            name: "stateProvinceName",
            fieldName: "stateProvinceName",
            minWidth: 400,
          },
        ]}
        groupNestingDepth={nestingDepth}
        item={item}
        itemIndex={itemIndex}
        selection={selection}
        selectionMode={SelectionMode.multiple}
      />
    ) : null;
  };

  return (
    <div>
      <SelectionZone selection={selection} selectionMode={SelectionMode.multiple}>
        <GroupedList
          items={items}
          onRenderCell={onRenderCell}
          selection={selection}
          selectionMode={SelectionMode.multiple}
          groups={groups}
          onShouldVirtualize={(props: IListProps) => false}
        />
      </SelectionZone>
    </div>
  );
};

export default RegionalGroupedList;
